import isNil from 'lodash/isNil'
import React from 'react'
import { Controller } from 'react-hook-form'

import { preventWheelInput } from '@/lib/helpers'

import { customOnChange, isNilOrNan } from './helpers'
import * as S from './styles'

import { FormItem } from '../FormItem'

import type { AmountSelectorProps } from './types'

export const AmountSelector = <T extends unknown>({
  label,
  control,
  shouldKeepLabelSpacing,
  name,
  min = 0,
  max = Infinity,
  error,
  hidden,
  rules,
  disabled = false,
  allowEmpty = false,
  asTextInput = false,
  postfix,
  ...restProps
}: AmountSelectorProps<T>) => (
  <FormItem
    shouldKeepLabelSpacing={shouldKeepLabelSpacing}
    label={label}
    error={error}
    hidden={hidden}
  >
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, value, ref } }) => {
        const isValueLessOrEqualMinimum = !isNil(min) && value <= min
        const isValueGreaterOrEqualMaximum = !isNil(max) && value >= max

        return (
          <S.Wrapper>
            {!asTextInput && (
              <S.ButtonDecrease
                hidden={disabled}
                type="button"
                onClick={() =>
                  !isValueLessOrEqualMinimum &&
                  onChange((isNilOrNan<T>(value) ? 0 : value) - 1)
                }
                disabled={isValueLessOrEqualMinimum || disabled}
                aria-label={`Decrease ${label} amount on 1`}
              />
            )}
            <S.Input
              $error={!!error}
              ref={ref}
              type="number"
              value={isNilOrNan<T>(value) ? '' : value}
              onChange={(evt) =>
                customOnChange(
                  evt,
                  Number(max),
                  Number(min),
                  allowEmpty,
                  onChange
                )
              }
              asTextInput={asTextInput}
              hasPostfix={!!postfix}
              onWheel={preventWheelInput}
              disabled={disabled}
              {...restProps}
            />
            {postfix && <S.Postfix>{postfix}</S.Postfix>}
            {!asTextInput && (
              <S.ButtonIncrease
                hidden={disabled}
                type="button"
                onClick={() =>
                  !isValueGreaterOrEqualMaximum &&
                  onChange((isNilOrNan<T>(value) ? 0 : value) + 1)
                }
                aria-label={`Increase ${label} amount on 1`}
                disabled={isValueGreaterOrEqualMaximum || disabled}
              />
            )}
          </S.Wrapper>
        )
      }}
    />
  </FormItem>
)

AmountSelector.displayName = 'AmountSelector'
